/**
 * Turning a body and a day's activity into calories.
 *
 * Two facts shape everything here. No wearable path available to this app
 * reports resting calories — Huawei publishes active calories only — so total
 * expenditure has to be assembled rather than read. And the same day's burn can
 * arrive from three places at once (a band summary, a screenshot, hand-logged
 * workouts), which is a double-counting trap rather than a redundancy benefit.
 */

import { daysBetween } from './date.js'
import { bmrMifflinStJeor, round, type Sex } from './macros.js'

/** Whole years on `on`, from an ISO `yyyy-mm-dd` birth date. */
export function ageYearsOn(birthDate: string, on: string): number | null {
  const birth = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthDate)
  const today = /^(\d{4})-(\d{2})-(\d{2})$/.exec(on)
  if (!birth || !today) return null

  const by = Number(birth[1])
  const bm = Number(birth[2])
  const bd = Number(birth[3])
  const ty = Number(today[1])
  const tm = Number(today[2])
  const td = Number(today[3])

  let age = ty - by
  if (tm < bm || (tm === bm && td < bd)) age--
  return age >= 0 && age < 130 ? age : null
}

/**
 * Everything you burn that is not resting and not a workout.
 *
 * Deliberately *not* Mifflin's textbook ladder. That one is defined by workouts
 * per week (1,55 = "3–5 treniruotės"), and this app also adds the day's logged
 * or watch-reported burn to the budget — so training paid twice, and a cut ran
 * at half the deficit it promised. Only one of the two may price exercise, and
 * the daily figure is the better one: it knows which days were rest days.
 *
 * So the ladder here asks about the shape of the day instead — a desk or a
 * warehouse — and workouts arrive separately, through `resolveActiveKcal`.
 * Stored on the user, so the estimate survives a reinstall.
 */
export const ACTIVITY_FACTORS = {
  /** Desk work, a car, a lift: the day itself adds almost nothing. */
  sedentary: 1.2,
  /** Some walking in it — school run, shops, a flight of stairs. */
  light: 1.35,
  /** On your feet most of the day: teaching, nursing, a shop floor. */
  moderate: 1.5,
  /** Physical work: building, warehouses, farms. Carrying things, all day. */
  heavy: 1.65,
} as const

export type ActivityLevel = keyof typeof ACTIVITY_FACTORS

export interface Profile {
  weightKg: number | null
  heightCm: number | null
  birthDate: string | null
  sex: Sex | null
  activityFactor: number | null
}

export interface RestingEnergy {
  bmr: number | null
  /** BMR × activity factor: expenditure before any logged workout. */
  tdee: number | null
  missing: Array<'weightKg' | 'heightCm' | 'birthDate' | 'sex'>
}

/**
 * Resting and habitual expenditure from the profile.
 *
 * Returns what is missing rather than a number-or-nothing, because the settings
 * screen has to be able to say "add your height" instead of silently showing a
 * dash.
 */
export function restingEnergy(profile: Profile, today: string): RestingEnergy {
  const missing: RestingEnergy['missing'] = []
  if (profile.weightKg == null) missing.push('weightKg')
  if (profile.heightCm == null) missing.push('heightCm')
  if (profile.birthDate == null) missing.push('birthDate')
  if (profile.sex == null) missing.push('sex')

  const age = profile.birthDate != null ? ageYearsOn(profile.birthDate, today) : null
  if (profile.birthDate != null && age == null && !missing.includes('birthDate')) {
    missing.push('birthDate')
  }

  if (profile.weightKg == null || profile.heightCm == null || profile.sex == null || age == null) {
    return { bmr: null, tdee: null, missing }
  }

  const bmr = bmrMifflinStJeor({
    weightKg: profile.weightKg,
    heightCm: profile.heightCm,
    ageYears: age,
    sex: profile.sex,
  })
  const factor = profile.activityFactor ?? ACTIVITY_FACTORS.sedentary
  return { bmr, tdee: Math.round(bmr * factor), missing }
}

export interface WorkoutBurn {
  kcal: number | null
}

export interface WearableSummary {
  provider: string
  activeKcal: number | null
}

export type ActiveKcalOrigin = 'wearable' | 'manual' | 'workouts' | 'none'

export interface ActiveKcalResolution {
  activeKcal: number
  origin: ActiveKcalOrigin
  /** Non-null when workouts were logged but a summary outranked them. */
  workoutKcalIgnored: number | null
}

const WEARABLE_PRECEDENCE = ['gadgetbridge', 'healthkit', 'huawei', 'huawei_ocr'] as const

/**
 * One day, one active-burn figure.
 *
 * A band's daily summary already contains the run you also logged by hand, so
 * these sources are ranked, never added. A device summary wins because it saw
 * the whole day; a number you typed wins over workouts because you typed it
 * knowing what you had done; hand-logged workouts are the fallback when nothing
 * else reported. The overruled workout total is returned rather than dropped so
 * the UI can explain why the ring does not match the sum of the sessions.
 */
export function resolveActiveKcal(
  summaries: readonly WearableSummary[],
  workouts: readonly WorkoutBurn[],
): ActiveKcalResolution {
  const workoutKcal = workouts.reduce((sum, w) => sum + (w.kcal ?? 0), 0)

  const ranked = summaries
    .filter((s) => s.activeKcal != null && s.activeKcal > 0)
    .sort((a, b) => rank(a.provider) - rank(b.provider))

  const best = ranked[0]
  if (best?.activeKcal != null) {
    return {
      activeKcal: round(best.activeKcal, 0),
      origin: best.provider === 'manual' ? 'manual' : 'wearable',
      workoutKcalIgnored: workoutKcal > 0 ? round(workoutKcal, 0) : null,
    }
  }

  if (workoutKcal > 0) {
    return { activeKcal: round(workoutKcal, 0), origin: 'workouts', workoutKcalIgnored: null }
  }
  return { activeKcal: 0, origin: 'none', workoutKcalIgnored: null }
}

function rank(provider: string): number {
  const index = WEARABLE_PRECEDENCE.indexOf(provider as (typeof WEARABLE_PRECEDENCE)[number])
  // 'manual' sits between the devices and nothing: trusted, but a device that
  // watched the whole day is trusted more.
  return index >= 0 ? index : provider === 'manual' ? WEARABLE_PRECEDENCE.length : 99
}

/** Grams of body fat per kcal — the 7 700 kcal/kg figure, per day. */
const KCAL_PER_KG = 7700

export type WeightGoal = 'lose' | 'maintain' | 'gain'

export interface SuggestedTargetInput {
  tdee: number
  goal: WeightGoal
  /** Intended change in kg per week; ignored when maintaining. */
  ratePerWeek?: number
}

export interface SuggestedTarget {
  kcalTarget: number
  /**
   * Signed daily difference from maintenance, for the explanation line — the
   * one the target actually implements, not the one that was asked for. They
   * differ whenever the floor below engages, and a line reading "−1 100 kcal"
   * over a target that is 300 below maintenance is worse than no line at all.
   */
  deltaKcal: number
  /** True when the arithmetic was clamped to a safe floor. */
  clamped: boolean
}

/**
 * A calorie target from maintenance and an intended rate.
 *
 * Clamped at 1 200 kcal because the arithmetic will happily suggest less to a
 * small person in a hurry, and a tracker should not be the thing that talks
 * someone into it.
 */
export function suggestKcalTarget({
  tdee,
  goal,
  ratePerWeek = 0.5,
}: SuggestedTargetInput): SuggestedTarget {
  const direction = goal === 'lose' ? -1 : goal === 'gain' ? 1 : 0
  const delta = direction === 0 ? 0 : Math.round((direction * ratePerWeek * KCAL_PER_KG) / 7)
  const raw = tdee + delta
  const kcalTarget = Math.max(1200, raw)
  return { kcalTarget, deltaKcal: kcalTarget - tdee, clamped: kcalTarget !== raw }
}

/**
 * What someone is doing right now, rather than what they weigh.
 *
 * A tracker that asks once for a goal is wrong within months: the same person
 * cuts, then holds, then builds, then cuts again. The phase is the control, and
 * everything below — the deficit, the protein floor, the fat floor — falls out
 * of it, so switching is one tap rather than a re-derivation the user has to do
 * on paper.
 */
export type GoalPhase = 'cut' | 'maintain' | 'bulk'

export interface PhaseDefaults {
  goal: WeightGoal
  ratePerWeek: number
  /**
   * Protein per kg of body weight. Highest on a cut, where it is the difference
   * between losing fat and losing the muscle underneath it; lowest on a bulk,
   * where the calories are not scarce and there is nothing to protect.
   */
  proteinPerKg: number
  /** A floor, not a target: below roughly 0,8 g/kg, hormones suffer. */
  fatPerKg: number
}

export const GOAL_PHASES: Readonly<Record<GoalPhase, PhaseDefaults>> = {
  cut: { goal: 'lose', ratePerWeek: 0.5, proteinPerKg: 2.2, fatPerKg: 0.8 },
  maintain: { goal: 'maintain', ratePerWeek: 0, proteinPerKg: 1.8, fatPerKg: 0.9 },
  bulk: { goal: 'gain', ratePerWeek: 0.25, proteinPerKg: 1.6, fatPerKg: 1.0 },
}

/**
 * The rates each phase offers. A bulk's are smaller than a cut's on purpose:
 * the body will shed half a kilo of fat in a week but cannot build half a kilo
 * of muscle, so a fast surplus is mostly a fast gain of what you are going to
 * have to lose again.
 */
export const PHASE_RATES: Readonly<Record<GoalPhase, readonly number[]>> = {
  cut: [0.25, 0.5, 0.75],
  maintain: [0],
  bulk: [0.125, 0.25, 0.5],
}

export interface PhasePlan {
  kcalTarget: number
  /** Signed daily difference from maintenance. */
  deltaKcal: number
  clamped: boolean
  proteinG: number
  fatG: number
  /** Whatever energy is left once the two floors are paid for. */
  carbsG: number
}

/**
 * A whole day's targets from a phase and a body.
 *
 * Protein and fat are set first because both are floors with a reason behind
 * them; carbohydrate is the remainder, which is also how anyone actually eats —
 * you do not decide to have 210 g of carbohydrate, you decide to have chicken
 * and then fill the rest of the plate.
 *
 * Protein is per kilogram of body weight, not lean mass, because body weight is
 * the number the app has. For someone with a lot of fat to lose that errs high,
 * which costs nothing but appetite.
 */
export function planForPhase(input: {
  phase: GoalPhase
  tdee: number
  weightKg: number
  /** Overrides the phase's own rate. Ignored while maintaining. */
  ratePerWeek?: number
}): PhasePlan {
  const defaults = GOAL_PHASES[input.phase]
  const rate = input.ratePerWeek ?? defaults.ratePerWeek
  const { kcalTarget, deltaKcal, clamped } = suggestKcalTarget({
    tdee: input.tdee,
    goal: defaults.goal,
    ratePerWeek: rate,
  })

  let proteinG = Math.round(input.weightKg * defaults.proteinPerKg)
  let fatG = Math.round(input.weightKg * defaults.fatPerKg)

  // A clamped target can be too small to pay for both floors. Protein is what
  // the phase exists to protect, so fat gives way rather than protein — but
  // only down to a quarter of the day's energy, because a plan that prescribes
  // 0 g of fat is not a plan, and past that point the target is simply too
  // small for the body it was computed for. Then protein gives way too: macros
  // that add up to more than their own calorie target are three numbers that
  // cannot all be met, which is worse advice than a lower protein figure.
  const minFatG = Math.min(fatG, Math.round((kcalTarget * 0.25) / 9))
  if (proteinG * 4 + fatG * 9 > kcalTarget) {
    fatG = Math.max(minFatG, Math.floor((kcalTarget - proteinG * 4) / 9))
  }
  if (proteinG * 4 + fatG * 9 > kcalTarget) {
    proteinG = Math.max(0, Math.floor((kcalTarget - fatG * 9) / 4))
  }

  const carbsG = Math.max(0, Math.round((kcalTarget - proteinG * 4 - fatG * 9) / 4))
  return { kcalTarget, deltaKcal, clamped, proteinG, fatG, carbsG }
}

/**
 * Which phase a stored target implies.
 *
 * The phase is deliberately not a column: a target below maintenance *is* a
 * cut, at exactly the rate the gap implies, so storing the word as well would
 * create two sources of truth that can disagree. The dead band keeps rounding
 * and a day's change in body weight from reading as a phase switch.
 */
export function phaseFromTarget(kcalTarget: number, tdee: number | null): GoalPhase {
  if (tdee == null) return 'maintain'
  const delta = kcalTarget - tdee
  if (delta <= -100) return 'cut'
  if (delta >= 100) return 'bulk'
  return 'maintain'
}

/**
 * How long the current phase has been running, and where it started.
 *
 * A cut is a period, not a setting. The difference matters because almost
 * everything that goes wrong with dieting is a function of duration rather than
 * of the deficit itself — adaptive thermogenesis, the leptin drop, the training
 * quality, the point at which someone quits — and none of it is visible in a
 * calorie target on its own. The app already has the evidence: goals are
 * effective-dated, so the run is the stretch of consecutive goals that all
 * imply the same phase.
 *
 * The phase of a historical goal is read against *today's* maintenance, which
 * is an approximation — the body it was set for weighed something else. It is
 * the right approximation: a 1 800 target set at 95 kg was a cut then and reads
 * as a deeper cut now, and both answers say "cut".
 */
export interface PhaseRun {
  phase: GoalPhase
  /** The date the current unbroken run of this phase began. */
  startedOn: string
  /** Whole weeks since then. */
  weeks: number
}

export function phaseRun(
  goals: readonly { effectiveFrom: string; kcalTarget: number }[],
  tdee: number | null,
  today: string,
): PhaseRun | null {
  const active = goals
    .filter((g) => g.effectiveFrom <= today)
    .sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom))
  const last = active[active.length - 1]
  if (!last) return null

  const phase = phaseFromTarget(last.kcalTarget, tdee)
  let startedOn = last.effectiveFrom
  for (let i = active.length - 2; i >= 0; i--) {
    const earlier = active[i]
    if (earlier == null || phaseFromTarget(earlier.kcalTarget, tdee) !== phase) break
    startedOn = earlier.effectiveFrom
  }
  return { phase, startedOn, weeks: Math.max(0, Math.floor(daysBetween(startedOn, today) / 7)) }
}

/**
 * Weeks of unbroken deficit after which a planned week or two at maintenance
 * earns its place.
 *
 * The MATADOR trial ran the same total deficit continuously and in two-week
 * blocks separated by two weeks of maintenance; the intermittent group lost
 * more fat and kept more of it. The mechanism is not mysterious — expenditure
 * falls as the diet goes on, and eating at maintenance for a fortnight puts
 * some of it back.
 */
export const DIET_BREAK_WEEKS = 6

/**
 * Past this, a cut has done what a cut can do in one run. Adherence is the
 * binding constraint by now rather than arithmetic, and the honest advice is to
 * hold the new weight for a while and start again later if there is more to do.
 */
export const LONG_CUT_WEEKS = 12

/**
 * A surplus held longer than this stops paying. Muscle accrues on a timescale
 * of months and fat does not wait for it, so the ratio of the two only gets
 * worse the longer a bulk runs.
 */
export const LONG_BULK_WEEKS = 16

export type PhaseAdvice = 'none' | 'diet_break' | 'to_maintenance' | 'long_bulk'

/** What, if anything, is worth saying about a phase this old. */
export function phaseAdvice(run: PhaseRun | null): PhaseAdvice {
  if (run == null) return 'none'
  if (run.phase === 'cut') {
    if (run.weeks >= LONG_CUT_WEEKS) return 'to_maintenance'
    if (run.weeks >= DIET_BREAK_WEEKS) return 'diet_break'
    return 'none'
  }
  if (run.phase === 'bulk' && run.weeks >= LONG_BULK_WEEKS) return 'long_bulk'
  return 'none'
}

export interface TrendPoint {
  date: string
  weightKg: number
}

/**
 * Exponentially-weighted body weight.
 *
 * A scale reading moves kilograms on water alone, so a raw series says nothing
 * about a 0.5 kg/week trend. The smoothing constant is one week's half-life,
 * which is the shortest window over which a real change is legible.
 *
 * The decay is per elapsed *day*, not per reading. Weighting each reading the
 * same made the half-life depend on how often someone stood on the scale: at
 * one reading a week the line lagged the body by two months and reported a
 * real 4 kg loss as barely one. Nobody weighs in every morning, and the chart
 * should not quietly punish them for it.
 */
export function smoothWeightSeries(points: readonly TrendPoint[], halfLifeDays = 7): TrendPoint[] {
  if (points.length === 0) return []
  const sorted = [...points].sort((a, b) => a.date.localeCompare(b.date))

  let value = sorted[0]?.weightKg ?? 0
  let previous: string | null = null
  return sorted.map((point) => {
    // No elapsed time carries no new information about the trend, so a second
    // reading on a date already seen leaves the line where it was.
    const elapsed = previous == null ? null : Math.max(0, daysBetween(previous, point.date))
    const alpha = elapsed == null ? 1 : 1 - 2 ** (-elapsed / halfLifeDays)
    value += alpha * (point.weightKg - value)
    previous = point.date
    return { date: point.date, weightKg: round(value, 2) }
  })
}

/**
 * kg/week, by least squares over the raw readings. Null below a fortnight.
 *
 * Deliberately not the difference between the smoothed endpoints: an
 * exponentially-weighted series lags by about its half-life at both ends, so
 * that method reports roughly a third less change than actually happened. The
 * smoothing is for the chart; the slope wants every reading.
 */
export function weightTrendPerWeek(points: readonly TrendPoint[]): number | null {
  const sorted = [...points].sort((a, b) => a.date.localeCompare(b.date))
  const first = sorted[0]
  const last = sorted[sorted.length - 1]
  if (!first || !last || sorted.length < 3) return null

  const dayOf = (date: string): number => Date.parse(`${date}T00:00:00Z`) / 86_400_000
  const span = dayOf(last.date) - dayOf(first.date)
  if (!Number.isFinite(span) || span < 14) return null

  const origin = dayOf(first.date)
  let sumX = 0
  let sumY = 0
  for (const p of sorted) {
    sumX += dayOf(p.date) - origin
    sumY += p.weightKg
  }
  const meanX = sumX / sorted.length
  const meanY = sumY / sorted.length

  let covariance = 0
  let variance = 0
  for (const p of sorted) {
    const dx = dayOf(p.date) - origin - meanX
    covariance += dx * (p.weightKg - meanY)
    variance += dx * dx
  }
  if (variance === 0) return null
  return round((covariance / variance) * 7, 2)
}

/** kcal/day the trend implies you are off maintenance by. */
export function impliedEnergyBalance(kgPerWeek: number): number {
  return Math.round((kgPerWeek * KCAL_PER_KG) / 7)
}

/**
 * Maintenance as the diary and the scale measured it, rather than as a formula
 * guessed it.
 *
 * Mifflin-St Jeor is a population average and the activity factor is a choice
 * from a list of four; both can be wrong by hundreds of calories for one
 * particular body. But if someone ate 2 100 kcal a day and lost half a kilo a
 * week, their maintenance was 2 650 — no equation required, and no way for it
 * to be wrong about them specifically.
 *
 * Needs both halves to be real: weeks of weights (`weightTrendPerWeek` returns
 * null below a fortnight) and days that were actually logged, since a day
 * nobody recorded is not a day of eating nothing.
 */
export function observedTdee(input: {
  /** Mean kcal over the days that were logged. */
  meanIntakeKcal: number | null
  /** Least-squares kg/week over the same stretch. */
  observedPerWeek: number | null
  /** How many days carried a diary entry — the evidence behind the mean. */
  loggedDays: number
  /** Below this many logged days the mean says too little to build on. */
  minLoggedDays?: number
}): number | null {
  const { meanIntakeKcal, observedPerWeek, loggedDays, minLoggedDays = 10 } = input
  if (meanIntakeKcal == null || observedPerWeek == null) return null
  if (loggedDays < minLoggedDays || meanIntakeKcal <= 0) return null
  return Math.round(meanIntakeKcal - impliedEnergyBalance(observedPerWeek))
}

export type TargetVerdict = 'unknown' | 'on_track' | 'faster' | 'slower'

export interface TargetCheck {
  verdict: TargetVerdict
  /** What the target would have to be for the scale to move as intended. */
  suggestedTarget: number | null
  /** suggestedTarget − currentTarget, so the UI can say "180 kcal less". */
  deltaKcal: number | null
}

/**
 * Does the scale agree with the target?
 *
 * The arithmetic behind a calorie target is an estimate stacked on an estimate:
 * Mifflin-St Jeor is a population average, the activity multiplier is a guess,
 * and label figures are allowed a ±20 % tolerance by law. The one measurement
 * that is not an estimate is body weight over time, so it gets the final word —
 * as advice. The target is never moved automatically: a fortnight of water
 * retention would otherwise walk someone's intake down for no reason, and a
 * number that changes by itself is a number nobody trusts.
 *
 * `null` for the observed trend (fewer than two weeks of weights) is 'unknown'
 * rather than 'on_track': having no evidence is not the same as agreeing.
 */
export function checkTargetAgainstTrend(input: {
  currentTarget: number
  /** Least-squares kg/week from the scale; null until there is enough of it. */
  observedPerWeek: number | null
  /** What the user asked for: negative to lose, positive to gain, 0 to hold. */
  intendedPerWeek: number
  /**
   * Maintenance as measured, when there is enough diary and enough scale to
   * measure it. Correcting from the target assumes the target was eaten, so
   * someone who runs 300 kcal over every day gets a correction computed from a
   * diet they never had; correcting from what they actually ate does not.
   */
  observedTdee?: number | null
  /** How far off before it is worth saying anything, in kg/week. */
  tolerance?: number
}): TargetCheck {
  const {
    currentTarget,
    observedPerWeek,
    intendedPerWeek,
    observedTdee = null,
    tolerance = 0.15,
  } = input
  if (observedPerWeek == null) return { verdict: 'unknown', suggestedTarget: null, deltaKcal: null }

  const difference = observedPerWeek - intendedPerWeek
  if (Math.abs(difference) <= tolerance) {
    return { verdict: 'on_track', suggestedTarget: null, deltaKcal: null }
  }

  // Losing faster than intended means eating more, not less: the correction is
  // always the energy equivalent of the gap, in the direction that closes it.
  const deltaKcal = -impliedEnergyBalance(difference)
  const corrected =
    observedTdee == null
      ? currentTarget + deltaKcal
      : observedTdee + impliedEnergyBalance(intendedPerWeek)
  const suggestedTarget = Math.max(1200, Math.round(corrected / 10) * 10)

  // "Faster" is about the goal, not about the sign. A bulk gaining 0,6 kg where
  // it meant to gain 0,25 is going faster than planned, even though the gap is
  // positive and a cut with the same positive gap is going slower. Read against
  // the intended direction; while maintaining, any movement at all is faster
  // than the nothing that was asked for.
  const progress =
    intendedPerWeek === 0 ? Math.abs(difference) : Math.sign(intendedPerWeek) * difference
  return {
    verdict: progress > 0 ? 'faster' : 'slower',
    suggestedTarget,
    deltaKcal: suggestedTarget - currentTarget,
  }
}
